// 2P AI — Composer / nuova sessione (§8.7), 1:1 con 2pai-composer.html. Cablato al backend reale.
// Autonomia → permission_mode: Solo lettura='read', Con conferma='gate', Autonomo='auto',
// Autonomo + skip totale='skip'. Niente combinazioni vietate (handoff v2). Orchestrazione: l'interruttore
// è presente (1:1) ma l'esecuzione orchestrata è la fase 10 → in R2 non viene inviata.
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { api } from '../lib/api';
import { withStepUp } from '../lib/webauthn';
import { useProjects, useServers } from '../lib/queries';
import { CommandBar } from '../components/CommandBar';
import { Crumb } from '../components/Crumb';
import { Icon } from '../components/Icon';

interface ModelInfo { id: string; display_name?: string; effort_levels?: string[]; supports_xhigh?: boolean }

type Autonomy = 'read' | 'confirm' | 'auto';
type Effort = 'low' | 'medium' | 'high' | 'xhigh';

function Seg<T extends string>({ value, onChange, options }: {
  value: T;
  onChange: (v: T) => void;
  options: { k: T; label: string; disabled?: boolean }[];
}) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button key={o.k} type="button" className={value === o.k ? 'sel' : undefined} disabled={o.disabled} onClick={() => onChange(o.k)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const AUTO_LABEL: Record<Autonomy, string> = {
  read: 'Solo lettura',
  confirm: 'Con conferma',
  auto: 'Autonomo',
};

/** `projects.default_autonomy` (read|gate|auto) → la leva del composer. */
function autonomyFromProject(v: string | undefined): Autonomy | null {
  if (v === 'read') return 'read';
  if (v === 'gate') return 'confirm';
  if (v === 'auto') return 'auto';
  return null;
}

const AUTO_HELP: Record<Autonomy, string> = {
  read: 'Legge, analizza e propone. Non tocca nulla: ogni intervento lo apri e lo guidi tu.',
  confirm: 'Lavora e prepara le modifiche, ma ognuna passa dal tuo OK prima di essere applicata.',
  auto: 'Applica le modifiche da solo, tranne nelle aree protette del progetto.',
};

// Ricorda le ultime scelte dell'operatore tra una sessione e l'altra (modello/effort/autonomia/skip).
const PREFS_KEY = 'plancia.session.prefs';
type SessionPrefs = { model?: string; effort?: Effort; autonomy?: Autonomy; skip?: boolean };
function loadPrefs(): SessionPrefs {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') as SessionPrefs; } catch { return {}; }
}

export function NewSession({ projectId: initialProjectId }: { projectId?: string } = {}) {
  const [, navigate] = useLocation();
  const projects = useProjects().data ?? [];
  const servers = useServers().data ?? [];
  const models = useQuery({
    queryKey: ['models'],
    queryFn: () => api.get<{ items: ModelInfo[] }>('/models').then((d) => d.items),
    retry: false,
  }).data ?? [];

  const [projectId, setProjectId] = useState(initialProjectId ?? '');
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState(() => loadPrefs().model ?? ''); // '' = default veloce (sonnet)
  const [effort, setEffort] = useState<Effort>(() => loadPrefs().effort ?? 'medium');
  const [autonomy, setAutonomy] = useState<Autonomy>(() => loadPrefs().autonomy ?? 'confirm');
  const [skipTotale, setSkipTotale] = useState<boolean>(() => loadPrefs().skip ?? false);
  const [identity, setIdentity] = useState<'claude_user' | 'root'>('claude_user');
  const [ultracode, setUltracode] = useState(false);
  const [orchestration, setOrchestration] = useState(false);
  const [advOpen, setAdvOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preseleziona il progetto d'origine (se si arriva da /p/:id/new); altrimenti il primo.
  useEffect(() => {
    if (!projectId && projects.length > 0) setProjectId(projects[0].id);
  }, [projects, projectId]);

  const project = projects.find((p) => p.id === projectId);
  const projectAutonomy = autonomyFromProject(project?.default_autonomy);

  // Cambiando progetto, la sua autonomia predefinita VINCE sull'ultima scelta ricordata: è il
  // motivo per cui la si configura. Resta modificabile a mano per questa sessione (il segnale
  // "Diverso dal predefinito" lo dice). Senza questo, un sito marcato "Solo lettura" si apriva in
  // "Autonomo" perché era l'ultima leva usata su un altro sito.
  useEffect(() => {
    if (projectAutonomy !== null) {
      setAutonomy(projectAutonomy);
      if (projectAutonomy !== 'auto') setSkipTotale(false);
    }
  }, [projectId, projectAutonomy]);
  const serverName = project ? servers.find((s) => s.id === project.server_id)?.name ?? project.server_id : '';

  // Modello risolto = scelto, oppure il più recente (models[0]).
  // Default veloce e DETERMINISTICO: preferisci un modello sonnet (rapido) invece di "il primo
  // dell'API" (che poteva essere opus → lento). L'operatore può sempre sceglierne un altro.
  const fastDefault = models.find((m) => /sonnet/i.test(m.id))?.id ?? models[0]?.id ?? '';
  // Modello predefinito del progetto: '' = nessuno, 'strongest' = «Sempre il più potente» (non è
  // un nome di modello: va tradotto, altrimenti Claude Code riceve la parola e la sessione muore).
  const projectModel = (() => {
    const want = project?.default_model ?? '';
    if (want === '') return '';
    if (want === 'strongest') {
      return models.find((m) => /opus/i.test(m.id))?.id
        ?? models.find((m) => m.supports_xhigh)?.id
        ?? models[0]?.id
        ?? '';
    }
    return models.some((m) => m.id === want) ? want : '';
  })();
  const resolvedModelId = model || projectModel || fastDefault;
  const selected = models.find((m) => m.id === resolvedModelId);
  const xhighOk = selected ? !!selected.supports_xhigh : true;
  const latestName = models[0]?.display_name ?? models[0]?.id ?? '';

  // Extra (xhigh) non disponibile su questo modello → ripiega su Alto.
  useEffect(() => {
    if (effort === 'xhigh' && !xhighOk) setEffort('high');
  }, [xhighOk, effort]);

  // skip totale si azzera al cambio livello.
  useEffect(() => {
    if (autonomy !== 'auto') setSkipTotale(false);
  }, [autonomy]);

  const permissionMode = autonomy === 'read' ? 'read' : autonomy === 'confirm' ? 'gate' : (skipTotale ? 'skip' : 'auto');
  const elevated = identity === 'root' || permissionMode === 'skip' || ultracode;
  const comboNote = ultracode || orchestration;

  const effortOptions = useMemo(
    () => ([
      { k: 'low' as Effort, label: 'Basso' },
      { k: 'medium' as Effort, label: 'Medio' },
      { k: 'high' as Effort, label: 'Alto' },
      { k: 'xhigh' as Effort, label: 'Extra', disabled: !xhighOk },
    ]),
    [xhighOk],
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!projectId || !prompt.trim()) return;
    if (resolvedModelId === '') { setError('Nessun modello disponibile.'); return; }
    setBusy(true);
    try {
      const body = {
        project_id: projectId,
        prompt: prompt.trim(),
        identity,
        permission_mode: permissionMode,
        model: resolvedModelId,
        effort,
        ultracode,
      };
      localStorage.setItem(PREFS_KEY, JSON.stringify({ model: resolvedModelId, effort, autonomy, skip: skipTotale }));
      const res = await withStepUp('start_session', (token) =>
        api.post<{ session: { id: string } }>('/sessions', body, token),
      );
      navigate(`/s/${res.session.id}`);
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : 'Errore');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <CommandBar />
      <div className="form">
        <Crumb current="Nuova sessione" onBack={() => navigate('/')} backLabel={project?.name ?? 'Progetti'} />
        <div className="ftitle">Nuova sessione</div>
        <div className="fsub">Affidi un compito a Claude Code, scegli con quale potenza lavora, e parti.</div>

        <form onSubmit={onSubmit}>
          {/* Destinazione */}
          <div className="fsection">
            <h3><Icon name="place_item" />Destinazione</h3>
            <div className="field">
              <label>Progetto</label>
              <div className="selwrap">
                <select className="sel" value={projectId} onChange={(e) => setProjectId(e.target.value)} required>
                  {projects.length === 0 ? <option value="">nessun progetto</option> : null}
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <Icon name="expand_more" />
              </div>
              {serverName ? (
                <div className="help" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9 }}>
                  Gira su <span className="srv-tag"><Icon name="dns" />{serverName}</span>
                </div>
              ) : null}
            </div>
          </div>

          {/* Compito */}
          <div className="fsection">
            <h3><Icon name="edit_note" />Compito</h3>
            <div className="field">
              <label>Cosa deve fare</label>
              <textarea
                className="inp"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                required
                placeholder="Es. Sistema il calcolo dell'IVA al checkout: ora arrotonda male sugli importi con sconto. Trova la causa, proponi la correzione e fammela vedere prima di applicarla."
              />
              <div className="help">Più sei chiaro sull'obiettivo, meglio lavora. Può fare domande se qualcosa non è chiaro.</div>
            </div>
          </div>

          {/* Modello e potenza */}
          <div className="fsection">
            <h3><Icon name="neurology" />Modello e potenza</h3>
            <div className="field">
              <label>Modello</label>
              {models.length > 0 ? (
                <div className="selwrap">
                  <select className="sel" value={model} onChange={(e) => setModel(e.target.value)}>
                    <option value="">Il più recente (consigliato)</option>
                    {models.map((m) => <option key={m.id} value={m.id}>{m.display_name ?? m.id}</option>)}
                  </select>
                  <Icon name="expand_more" />
                </div>
              ) : (
                <input className="inp mono" type="text" value={model} onChange={(e) => setModel(e.target.value)} placeholder="id modello" />
              )}
              <div className="help">
                Resta sempre aggiornato da solo: quando esce un modello nuovo parte da quello.
                {latestName ? <> Ora parte da <span className="mono">{latestName}</span>.</> : null} Puoi cambiarlo in ogni sessione.
              </div>
            </div>
            <div className="field">
              <label>Sforzo di ragionamento</label>
              <Seg value={effort} onChange={setEffort} options={effortOptions} />
              <div className="help">
                Più sforzo significa ragionamento più profondo sui problemi difficili, ma consuma di più.{' '}
                {xhighOk ? "L'opzione Extra è disponibile solo sui modelli che la supportano." : 'Questo modello non supporta lo sforzo Extra.'}
              </div>
            </div>
          </div>

          {/* Autonomia */}
          <div className="fsection">
            <h3><Icon name="shield" />Autonomia per questa sessione</h3>
            <div className="field">
              <label>Quanto può fare da solo</label>
              <Seg
                value={autonomy}
                onChange={setAutonomy}
                options={[
                  { k: 'read', label: 'Solo lettura' },
                  { k: 'confirm', label: 'Con conferma' },
                  { k: 'auto', label: 'Autonomo' },
                ]}
              />
              {/* "Ereditato da X" solo quando è VERO: prima compariva sempre, anche mentre il
                  composer mostrava l'ultima scelta fatta altrove — e un sito configurato "Solo
                  lettura" partiva in "Autonomo" con scritto sotto che ereditava dal progetto. */}
              <div className="help">
                {AUTO_HELP[autonomy]}
                {project && projectAutonomy !== null ? (
                  autonomy === projectAutonomy
                    ? <span style={{ color: 'var(--text-mid)' }}> Ereditato da {project.name}.</span>
                    : <span style={{ color: 'var(--warn)' }}> Diverso dal predefinito di {project.name} ({AUTO_LABEL[projectAutonomy]}).</span>
                ) : null}
              </div>
            </div>
            {autonomy === 'auto' ? (
              <div className="field" style={{ marginBottom: 0 }}>
                <div className={skipTotale ? 'toggle-card hot' : 'toggle-card'}>
                  <div>
                    <div className="tt" style={{ color: 'var(--err)' }}>Salta anche le aree protette</div>
                    <div className="td">Skip totale dei permessi: nessuna conferma, nemmeno su <span className="mono">.env</span>, checkout o deploy. Claude Code applica ogni cosa da solo.</div>
                  </div>
                  <button
                    type="button"
                    className={skipTotale ? 'switch danger on' : 'switch danger'}
                    role="switch"
                    aria-checked={skipTotale}
                    aria-label="Salta anche le aree protette"
                    onClick={() => setSkipTotale((v) => !v)}
                  />
                </div>
                {skipTotale ? (
                  <div className="note danger"><Icon name="warning" /><span><b>Nessuna rete di sicurezza.</b> Non ti viene chiesto niente. Usala solo per compiti che conosci bene e fuori dalla produzione; all'avvio ti verrà chiesta una conferma esplicita.</span></div>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Avanzate */}
          <button type="button" className="adv-head" aria-expanded={advOpen} onClick={() => setAdvOpen((v) => !v)}>
            <Icon name="tune" className="lead" />
            <span className="t"><span className="l1">Opzioni avanzate</span><span className="l2">Identità di esecuzione, ultracode, orchestrazione</span></span>
            <Icon name="expand_more" className="chev" />
          </button>
          <div className="adv-body" hidden={!advOpen}>
            <div className="fsection">
              <h3><Icon name="person" />Identità di esecuzione</h3>
              <div className="field">
                <label>Con quale utente lavora sulla macchina</label>
                <Seg
                  value={identity}
                  onChange={setIdentity}
                  options={[{ k: 'claude_user', label: 'claude-user' }, { k: 'root', label: 'root' }]}
                />
                {identity === 'root' ? (
                  <div className="note warn"><Icon name="warning" /><span><b>root</b> ha accesso totale alla macchina, anche fuori dalle cartelle consentite. Usalo solo se un compito lo richiede davvero.</span></div>
                ) : null}
              </div>
            </div>

            <div className="fsection">
              <h3><Icon name="rocket_launch" />Modalità di lavoro</h3>
              <div className="toggle-card">
                <div><div className="tt">Ultracode</div><div className="td">Massimo sforzo di ragionamento, per i problemi più ostici. Più lento e consuma di più.</div></div>
                <button type="button" className={ultracode ? 'switch on' : 'switch'} role="switch" aria-checked={ultracode} aria-label="Ultracode" onClick={() => setUltracode((v) => !v)} />
              </div>
              <div className="toggle-card">
                <div><div className="tt">Orchestrazione</div><div className="td">Un agente capo replica il brief e coordina più agenti in parallelo. Per lavori grandi divisibili in parti.</div></div>
                <button type="button" className={orchestration ? 'switch on' : 'switch'} role="switch" aria-checked={orchestration} aria-label="Orchestrazione" onClick={() => setOrchestration((v) => !v)} />
              </div>
              {comboNote ? (
                <div className="note info"><Icon name="info" /><span>Ultracode e orchestrazione danno il meglio in autonomia. In modalità <b>Autonomo</b> le modifiche vengono applicate da sole; restano sotto il tuo OK solo le <b>aree protette</b> del progetto, e vale comunque il confine delle cartelle consentite.</span></div>
              ) : null}
            </div>
          </div>

          {error ? <div className="note danger"><Icon name="error" /><span>{error}</span></div> : null}
          {elevated && !error ? (
            <div className="note warn"><Icon name="fingerprint" /><span>Sessione ad alto privilegio: all'avvio verrà chiesto lo step-up biometrico.</span></div>
          ) : null}

          <div className="actionbar">
            <button type="button" className="btn-ghost" onClick={() => navigate('/')}>Annulla</button>
            <button type="submit" className="btn-primary" disabled={busy || !projectId || !prompt.trim()}>
              <Icon name="rocket_launch" />{busy ? '…' : 'Avvia sessione'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
